import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Card,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { ThemeProvider } from '@mui/material/styles';

import { CustomIconButton, DefaultContainedButton, RedOutlinedButton } from '../../../../utils/Buttons';
import { DecisionDialog } from '../../../../utils/DecisionDialog';
import { EditPositionDialog } from '../../../../utils/EditPositionDialog';
import { ErrorUI, HttpErrorHandler } from '../../../../utils/ErrorHandler';
import { LoadingUI } from '../../../../utils/LayoutStyles';
import { NewPositionDialog } from '../../../../utils/NewPositionDialog';
import { TablePaginationActions, theme } from '../../../../utils/TableStyles';
import API from '../../../../api';
import { PositionStatusLabel } from '../../../../utils/PositionStatusLabel';
import { useUser } from '../context/UserContext';
import { usePopup } from '../../../../context/PopupContext';

export default function PositionsTab() {
  const { showPopup, setPopUpText } = usePopup();
  const { isLoading, errorMessage, userInfo, setErrorMessage, positions, setPositions, canModifyUser } = useUser();

  const is600Screen = useMediaQuery('(min-width: 600px)');

  const [intPositions, setIntPositions] = useState([]);
  const [extPositions, setExtPositions] = useState([]);
  const [displayedPositions, setDisplayedPositions] = useState([]);

  // Filter positions by internal & external
  useEffect(() => {
    const internalPositions = [];
    const externalPositions = [];

    positions.map((position) => {
      if (position.internal === 1) {
        internalPositions.push(position);
      } else {
        externalPositions.push(position);
      }

      return null;
    });

    setIntPositions(internalPositions);
    setExtPositions(externalPositions);
  }, [positions]);

  /* ------------------------------- New Position ------------------------------- */
  const [openNewPosition, setOpenNewPosition] = useState(false);

  // Open NewPosition Dialog
  const handleOpenNewPosition = () => {
    setOpenNewPosition(true);
  };

  // Close NewPosition Dialog
  const handleCloseNewPosition = () => {
    setOpenNewPosition(false);
  };

  // Get User Positions
  const getUserPositions = () => {
    API.get(`/users/${userInfo.id}`)
      .then((response) => {
        setPositions(response.data.positions);
      })
      .catch((error) => {
        HttpErrorHandler(error, setErrorMessage);
      });
  };

  /* ------------------------------- Edit Position ------------------------------ */
  const [openEditPosition, setOpenEditPosition] = useState(false);
  const [positionToEdit, setPositionToEdit] = useState('');

  // Open Edit Position Dialog
  const handleOpenEditPosition = (position) => {
    setOpenEditPosition(true);
    setPositionToEdit(position);
  };

  // Close Edit Position Dialog
  const handleCloseEditPosition = () => {
    setOpenEditPosition(false);
  };

  /* ------------------------------ Charge Delete ----------------------------- */
  const [openDeleteCharge, setOpenDeleteCharge] = useState(false);

  // open delete position dialog
  const onOpenDeleteChargeDialog = (position) => {
    setPositionToEdit(position);
    setOpenDeleteCharge(true);
  };

  // close delete position dialog
  const onCloseDeleteChargeDialog = () => {
    setPositionToEdit('');
    setOpenDeleteCharge(false);
  };

  // delete position
  const onDeleteCharge = (event) => {
    event.preventDefault();

    API.delete(`/charges/${positionToEdit.id}`)
      .then((response) => {
        getUserPositions();
        onCloseDeleteChargeDialog();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // position tab
  const [positionTabValue, setPositionTabValue] = useState(0);
  const handleChangePositionTab = (event, newValue) => {
    setPositionTabValue(newValue);
  };

  // Set displayed positions Tabs
  useEffect(() => {
    if (positionTabValue === 0) {
      setDisplayedPositions(extPositions);
    } else {
      setDisplayedPositions(intPositions);
    }
  }, [positionTabValue, intPositions, extPositions]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(is600Screen ? 10 : 5);
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - positions.length) : 0;

  // change page
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // change rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  return (
    <>
      {isLoading ? (
        LoadingUI()
      ) : errorMessage.length === 0 ? (
        <Box>
          <Stack flexDirection="row" alignItems="center" justifyContent="end" sx={{ paddingBottom: '24px' }}>
            {/* New position button */}
            <DefaultContainedButton disabled={!canModifyUser} onClick={handleOpenNewPosition} startIcon={<AddIcon />}>
              Aggiungi Carica
            </DefaultContainedButton>

            <NewPositionDialog
              openDialog={openNewPosition}
              closeDialog={handleCloseNewPosition}
              userAuth={'admin'}
              userInfo={userInfo}
              refreshFunction={getUserPositions}
            />
          </Stack>

          {/* Positions card */}
          <Card>
            {/* Filter Tabs */}
            <Tabs
              variant="fullWidth"
              value={positionTabValue}
              onChange={handleChangePositionTab}
              scrollButtons="auto"
              allowScrollButtonsMobile
              textColor="secondary"
              indicatorColor="secondary"
              sx={{ boxShadow: 'rgba(145, 158, 171, 0.08) 0px -2px 0px 0px inset', padding: '0px 24px 0' }}
            >
              <Tab
                disableRipple
                icon={
                  <Typography
                    sx={{
                      backgroundColor: positionTabValue === 0 ? '#000000' : '#edeff1',
                      borderRadius: '6px',
                      color: positionTabValue === 0 ? '#ffffff' : '#637381',
                      padding: '2px 8px',
                      fontSize: '1em',
                      fontWeight: 'bold',
                    }}
                  >
                    {extPositions.length}
                  </Typography>
                }
                iconPosition="end"
                label="Cariche Istituzionali"
              />
              <Tab
                disableRipple
                icon={
                  <Typography
                    sx={{
                      backgroundColor: positionTabValue === 1 ? '#22c55e' : '#dbf6e5',
                      borderRadius: '6px',
                      color: positionTabValue === 1 ? '#ffffff' : '#118d57',
                      padding: '2px 8px',
                      fontSize: '1em',
                      fontWeight: 'bold',
                    }}
                  >
                    {intPositions.length}
                  </Typography>
                }
                iconPosition="end"
                label="Cariche Politiche"
              />
            </Tabs>

            {/* Table */}
            <TableContainer sx={{ padding: '24px' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Stato Carica</TableCell>
                    <TableCell>Carica</TableCell>
                    {positionTabValue === 0 && <TableCell>Email Istituzionale</TableCell>}
                    {positionTabValue === 1 && <TableCell>Entità</TableCell>}
                    <TableCell>Data Inizio</TableCell>
                    <TableCell>Data Fine</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>

                {displayedPositions.length > 0 ? (
                  <TableBody>
                    <EditPositionDialog
                      openDialog={openEditPosition}
                      closeDialog={handleCloseEditPosition}
                      userAuth={'admin'}
                      userInfo={userInfo}
                      positionToEdit={positionToEdit}
                      setPositionToEdit={setPositionToEdit}
                      refreshFunction={getUserPositions}
                    />

                    {/* Delete Position Dialog */}
                    <DecisionDialog
                      openDialog={openDeleteCharge}
                      dialogTitle={'Elimina carica'}
                      dialogDescription={`Sei sicuro di voler eliminare la carica: ${positionToEdit.name} ?`}
                      closeOperation={onCloseDeleteChargeDialog}
                      requestOperation={onDeleteCharge}
                      requestText={'Elimina'}
                      closeText={'Chiudi'}
                      RequestButton={RedOutlinedButton}
                    />

                    {displayedPositions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((position) => (
                      <TableRow key={position.id}>
                        <TableCell>{PositionStatusLabel(position, 'label')}</TableCell>

                        <TableCell>{position.name}</TableCell>

                        {positionTabValue === 0 && <TableCell>{position.email ? position.email : '/'}</TableCell>}

                        {positionTabValue === 1 && <TableCell>{position.entity_name}</TableCell>}

                        <TableCell>
                          {position.start_date ? dayjs(position.start_date).format('DD/MM/YYYY') : '/'}
                        </TableCell>

                        <TableCell>{position.end_date ? dayjs(position.end_date).format('DD/MM/YYYY') : '/'}</TableCell>

                        <TableCell align="right" sx={{ padding: '4px', paddingLeft: '16px', maxWidth: '150px' }}>
                          <Stack direction="row" justifyContent="end" alignItems="center" gap="12px" maxWidth="150px">
                            {/* Edit Position */}
                            <Box>
                              <Tooltip title="Modifica Carica" arrow placement="top">
                                <Box>
                                  <CustomIconButton
                                    disabled={!canModifyUser}
                                    onClick={() => handleOpenEditPosition(position)}
                                  >
                                    <EditIcon />
                                  </CustomIconButton>
                                </Box>
                              </Tooltip>
                            </Box>

                            {/* Delete Position */}
                            <Box>
                              <Tooltip title="Elimina Carica" arrow placement="top">
                                <Box>
                                  <CustomIconButton
                                    disabled={!canModifyUser}
                                    onClick={() => onOpenDeleteChargeDialog(position)}
                                  >
                                    <DeleteIcon />
                                  </CustomIconButton>
                                </Box>
                              </Tooltip>
                            </Box>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* empty table rows */}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 57 * emptyRows }}>
                        <TableCell colSpan={8} />
                      </TableRow>
                    )}
                  </TableBody>
                ) : (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        Nessuna carica disponibile
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}

                {/*  pagination */}
                <TableFooter>
                  <TableRow>
                    <ThemeProvider theme={theme}>
                      <TablePagination
                        sx={{ borderBottom: 'none' }}
                        rowsPerPageOptions={[]}
                        colSpan={8}
                        count={positions.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Righe per Pagina"
                        ActionsComponent={TablePaginationActions}
                      />
                    </ThemeProvider>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Card>
        </Box>
      ) : (
        // Error handler
        ErrorUI(errorMessage)
      )}
    </>
  );
}
